import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, apiService, DISCOVERY_SOURCES, type DiscoverySource } from '../../services/ApiService';
import { Brand, Header } from '../Chat/Header';

/** The optional question of the first connection, how the person heard of Sculpt: shown once, between the sign-in and
 *  the rest (`GET /user` → `discovery_asked` false), whichever way they signed in. The portal asks the same one for
 *  those who meet Sculpt there. */
export function DiscoveryQuestion({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const [source, setSource] = useState<DiscoverySource | null>(null);
  const [busy, setBusy] = useState(false);

  const answer = async (answered: DiscoverySource | null) => {
    if (busy) return;
    setBusy(true);
    try {
      await apiService.answerDiscovery(answered);
    } catch (err) {
      // An expired session goes back to the login (App.tsx). Otherwise, an optional question holds nobody back: it comes
      // again the next time the pane opens.
      if (err instanceof ApiError && err.status === 401) return;
    }
    onDone();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (source) void answer(source);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-pane">
      <Header left={<Brand title={t('app.title')} />} />
      <form onSubmit={submit} className="flex flex-1 flex-col justify-center gap-5 px-4 pb-8">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[20px] font-medium leading-[1.2] tracking-[-0.02em] text-ink">{t('discovery.title')}</h1>
          <p className="text-[13px] leading-[1.45] text-muted">{t('discovery.subtitle')}</p>
        </div>
        <fieldset className="flex flex-col gap-1.5">
          <legend className="sr-only">{t('discovery.title')}</legend>
          {DISCOVERY_SOURCES.map((s) => (
            <label key={s} className={`flex cursor-pointer items-center gap-2 rounded-[5px] border bg-white px-3 py-2 text-[12.5px] text-ink hover:bg-hover ${source === s ? 'border-accent' : 'border-line-btn'}`}>
              <input type="radio" name="discovery-source" value={s} checked={source === s} onChange={() => setSource(s)} className="text-accent focus:ring-0" />
              {t(`discovery.sources.${s}`)}
            </label>
          ))}
        </fieldset>
        <div className="flex flex-col gap-2">
          <button type="submit" disabled={busy || !source} className="rounded-[5px] bg-accent py-2 text-center text-[12.5px] font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-send-idle">
            {t('discovery.continue')}
          </button>
          <button type="button" onClick={() => void answer(null)} disabled={busy} className="rounded-[5px] py-1.5 text-center font-mono text-[11px] text-muted hover:text-ink disabled:cursor-not-allowed">
            {t('discovery.skip')}
          </button>
        </div>
      </form>
    </div>
  );
}
